import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, Switch, FormControlLabel,
  Typography, CircularProgress, Checkbox, ListItemText,
} from '@mui/material';
import { PricingModelResponse, DeliveryPricingRuleResponse, CreateDeliveryPricingRuleRequest, DateOnly, RetailerHeaderModel, UserFranchiseList } from '../../../app/services/api/ofdClient';
import { client, DeliveryMethod } from '../../../app/services/api/orderManagementClient';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';

const dateOnlyToString = (d?: DateOnly | string): string => {
  if (!d) return '';

  if (typeof d === 'string') {
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  }

  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
};

const isIsoDate = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

interface Props {
  rule: DeliveryPricingRuleResponse | null;
  models: PricingModelResponse[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const RuleEditor: React.FC<Props> = ({ rule, models, open, onClose, onSaved }) => {
  const isEdit = !!rule;
  const { t } = useTranslation('deliveryPricing');
  const { selectedFranchise, selectedStore } = useContext(UserContext);
  const { deliveryPricingClient, getRetailersList } = useNswagClient();
  const getRetailersListRef = useRef(getRetailersList);

  useEffect(() => {
    getRetailersListRef.current = getRetailersList;
  }, [getRetailersList]);

  const [pricingModelId, setPricingModelId] = useState<number>(0);
  const [shippingMethodId, setShippingMethodId] = useState('');
  const [shippingMethods, setShippingMethods] = useState<DeliveryMethod[]>([]);
  const [retailers, setRetailers] = useState<RetailerHeaderModel[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [retailerIds, setRetailerIds] = useState<number[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [priority, setPriority] = useState('0');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    if (!open) return;
    setLoadingMethods(true);
    const currentFranchiseName = selectedFranchise?.franchiseName ?? '';
    const userFranchiseList: UserFranchiseList = {
      // Scope to the current store's franchise only. The API's tenant-isolation check (403) is enforced
      // against the current store's ExternalCustomerTypeId, so offering retailers from other franchises
      // the user happens to have access to would let them pick one that fails on save.
      franchiseNames: currentFranchiseName ? [currentFranchiseName] : [],
      otherRegions: false,
      currentFranchiseName,
    };
    const storeNumber = selectedStore?.storeNumber?.toString();

    Promise.all([
      client.getOnboardingDeliveryMethods(storeNumber).then(result => result?.data?.ShippingMethodList ?? []),
      getRetailersListRef.current(userFranchiseList),
    ])
      .then(([methods, retailerResult]) => {
        setShippingMethods(methods ?? []);
        setRetailers(retailerResult.retailerList ?? []);
      })
      .catch(() => {
        setShippingMethods([]);
        setRetailers([]);
      })
      .finally(() => setLoadingMethods(false));
  }, [open, selectedStore?.storeNumber, selectedFranchise?.franchiseName]);

  const sortedRetailers = useMemo(
    () => retailers
      .filter((r): r is typeof r & { retailerId: number } => r.retailerId != null)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [retailers],
  );

  useEffect(() => {
    if (rule) {
      setPricingModelId(rule.pricingModelId ?? 0);
      setShippingMethodId((rule.shippingMethodId ?? '').toString());
      setRetailerIds(rule.retailerIds ?? []);
      setEffectiveFrom(dateOnlyToString(rule.effectiveFrom));
      setEffectiveTo(dateOnlyToString(rule.effectiveTo));
      setPriority((rule.priority ?? 0).toString());
      setEnabled(rule.enabled ?? true);
    } else {
      setPricingModelId(models.length > 0 ? (models[0].pricingModelId ?? 0) : 0);
      setShippingMethodId('');
      setRetailerIds([]);
      setEffectiveFrom(new Date().toISOString().split('T')[0]);
      setEffectiveTo('');
      setPriority('0');
      setEnabled(true);
    }
  }, [rule, open, models]);

  const handleSave = async () => {
    setError('');
    if (!pricingModelId) { setError(t('ruleEditor.modelRequired', 'Pricing model is required')); return; }
    if (!shippingMethodId) { setError(t('ruleEditor.shippingRequired', 'Shipping method ID is required')); return; }
    if (!effectiveFrom) { setError(t('ruleEditor.effectiveFromRequired', 'Effective from date is required')); return; }

    if (!isIsoDate(effectiveFrom)) {
      setError(t('ruleEditor.effectiveFromInvalid', 'Effective from date is invalid'));
      return;
    }

    if (effectiveTo && !isIsoDate(effectiveTo)) {
      setError(t('ruleEditor.effectiveToInvalid', 'Effective to date is invalid'));
      return;
    }

    if (!isEdit && effectiveFrom < today) {
      setError(t('ruleEditor.effectiveFromPast', 'Effective from cannot be before today'));
      return;
    }

    if (!isEdit && effectiveTo && effectiveTo < today) {
      setError(t('ruleEditor.effectiveToPast', 'Effective to cannot be before today'));
      return;
    }

    if (effectiveTo && effectiveTo < effectiveFrom) {
      setError(t('ruleEditor.effectiveRangeInvalid', 'Effective to must be on or after effective from'));
      return;
    }

    const request: CreateDeliveryPricingRuleRequest = {
      pricingModelId,
      shippingMethodId: parseInt(shippingMethodId),
      retailerIds,
      effectiveFrom: effectiveFrom as unknown as DateOnly,
      effectiveTo: effectiveTo ? (effectiveTo as unknown as DateOnly) : undefined,
      priority: parseInt(priority) || 0,
      enabled,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await deliveryPricingClient.rulesPUT(rule!.deliveryPricingRuleId!, request);
      } else {
        await deliveryPricingClient.rulesPOST(request);
      }
      onSaved();
    } catch (ex: unknown) {
      const apiMessage = (ex as { response?: { data?: string } })?.response?.data;
      setError(apiMessage || t('ruleEditor.saveFailed', 'Failed to save rule'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth>
      <DialogTitle>{isEdit ? t('ruleEditor.editTitle', 'Edit Delivery Pricing Rule') : t('ruleEditor.createTitle', 'Create Delivery Pricing Rule')}</DialogTitle>
      <DialogContent>
        <Grid container
          spacing={2}
          sx={{ mt: 1 }}>
          <Grid item
            xs={12}>
            <TextField
              select
              fullWidth
              required
              label={t('ruleEditor.pricingModel', 'Pricing Model')}
              value={pricingModelId}
              onChange={e => setPricingModelId(Number(e.target.value))}
            >
              {models.map(m => (
                <MenuItem
                  key={m.pricingModelId}
                  value={m.pricingModelId}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item
            xs={12}>
            {loadingMethods ? (
              <CircularProgress size={20} />
            ) : (
              <TextField
                select
                fullWidth
                required
                label={t('ruleEditor.shippingMethod', 'Shipping Method')}
                value={shippingMethodId}
                onChange={e => setShippingMethodId(e.target.value)}
              >
                {shippingMethods.map(m => (
                  <MenuItem
                    key={m.ShippingMethodId}
                    value={m.ShippingMethodId}>
                    {m.Name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item
            xs={12}>
            <TextField
              select
              fullWidth
              label={t('ruleEditor.retailer', 'Retailers')}
              value={retailerIds}
              onChange={e => {
                const value = e.target.value;
                setRetailerIds(typeof value === 'string'
                  ? value.split(',').filter(Boolean).map(Number)
                  : (value as number[]));
              }}
              helperText={t('ruleEditor.retailerHelp', 'Select retailers or leave empty for global')}
              FormHelperTextProps={{ sx: { ml: 0 } }}
              placeholder={t('ruleEditor.retailerPlaceholder', 'Leave empty for global')}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (selected as number[])
                  .map(id => sortedRetailers.find(r => r.retailerId === id)?.name ?? id)
                  .join(', '),
              }}
            >
              {sortedRetailers.map(r => (
                <MenuItem
                  key={r.retailerId}
                  value={r.retailerId}>
                  <Checkbox checked={retailerIds.includes(r.retailerId)} />
                  <ListItemText primary={r.name ?? ''} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item
            xs={6}>
            <DatePickerLocalized
              label={`${t('ruleEditor.effectiveFrom', 'Effective From')} *`}
              value={effectiveFrom}
              useCalendar={false}
              minDate={isEdit ? undefined : dayjs(today)}
              maxDate={effectiveTo ? dayjs(effectiveTo) : undefined}
              onChange={(value) => {
                const next = value ?? '';
                setEffectiveFrom(next);
                if (effectiveTo && next && effectiveTo < next) {
                  setEffectiveTo('');
                }
              }}
            />
          </Grid>
          <Grid item
            xs={6}
            sx={{
              '& .MuiFormControl-root.MuiTextField-root': {
                marginBottom: 0,
              },
            }}>
            <DatePickerLocalized
              label={t('ruleEditor.effectiveTo', 'Effective To')}
              value={effectiveTo}
              useCalendar={false}
              minDate={isEdit ? (effectiveFrom ? dayjs(effectiveFrom) : undefined) : dayjs(effectiveFrom || today)}
              onChange={(value) => setEffectiveTo(value ?? '')}
            />
            <Typography
              variant='textXS'
              color='text.secondary'
              sx={{ mt: 0, ml: 0 }}>
              {t('ruleEditor.effectiveToHelp', 'Leave blank for no end date')}
            </Typography>
          </Grid>
          <Grid item
            xs={6}>
            <TextField
              fullWidth
              type='number'
              label={t('ruleEditor.priority', 'Priority')}
              value={priority}
              onChange={e => setPriority(e.target.value)}
              helperText={t('ruleEditor.priorityHelp', 'Higher = takes precedence within same scope')}
              FormHelperTextProps={{ sx: { ml: 0 } }}
              placeholder={t('ruleEditor.priorityPlaceholder', 'e.g. 0')}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item
            xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                />
              }
              label={t('ruleEditor.enabled', 'Enabled')}
            />
          </Grid>

          {error && (
            <Grid item
              xs={12}>
              <Typography
                color='error'
                variant='textSM'>
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ruleEditor.cancel', 'Cancel')}</Button>
        <Button
          variant='primary'
          onClick={handleSave}
          disabled={saving}>
          {saving ? t('ruleEditor.saving', 'Saving...') : t('ruleEditor.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleEditor;
